import React, { Fragment, useContext, useRef } from "react";
import { useLocation, useParams } from "react-router-dom";
import Button from "@/components/ui/Button.jsx";
import Textarea from "@/components/ui/Textarea.jsx";
import Select from "react-select";
import "rc-slider/assets/index.css";
import Textinput from "@/components/ui/Textinput.jsx";
import Card from "@/components/ui/Card.jsx";
import Tooltip from "@/components/Tooltip";
import { Tab } from "@headlessui/react";
import LeadFields from "@/pages/chatbot/settings/LeadFields";
import ModelSettings from "@/pages/chatbot/settings/ModelSettings";
import { languages } from "@/utils/lang";
import { useTranslation } from "react-i18next";
import { Leads } from "@/models/chatbot.ts";
import { ChatbotContext } from "@/store/context/ChatbotContext.tsx";
import { saveSettings } from "@/store/chatbot.ts";
import Loading from "@/components/Loading";
import { WorkSchedule } from "@/components/WorkSchedule.tsx";
import Branding from "@/utils/branding.ts";

export interface RateLimits {
  message: string;
  dailyLimit: number;
  limit: number;
  period: number;
}

export interface UnknownQuestions {
  phrases: string;
}

let lang = Branding().language;
let showSchedule = lang === "ru";

const languagesList = [
  { value: "", label: "Auto: use a context of a dialog (Default)" },
  {
    value: "auto:last-question",
    label: "Auto: language detection from the last phrase",
  },
].concat(
  languages.map((item) => {
    return { value: item.name, label: item.name };
  }),
);

const ChatBotSettings = () => {
  const { chatSettings, setSettings, viewChatbot, invalidateChatbot } =
    useContext(ChatbotContext);

  const { t } = useTranslation();
  const { chatbotID } = useParams();
  const location = useLocation();
  const leadFormRef = useRef();

  let tabs: any[] = [
    { title: t("settings_general") },
    { title: t("sidebar.ai_settings") },
    { title: t("settings.work_schedule"), show: showSchedule },
    { title: t("sidebar.leads"), isHeader: true },
    { title: t("sidebar.leads"), nested: true },
    { title: t("settings.access"), isHeader: true },
    { title: t("settings.domains"), nested: true },
    { title: t("settings.rate_limiting"), nested: true },
  ];

  if (!chatSettings || !viewChatbot) {
    return <Loading />;
  }

  return (
    <div className="relative">
      <Card title="Settings">
        <Tab.Group>
          <div className="flex flex-row flex-1">
            <div className="min-w-[250px] border-r-2 mr-6 h-full flex flex-col">
              <Tab.List>
                {tabs.map((item, i) => {
                  if (item.show !== undefined && !item.show) {
                    return null;
                  }

                  if (item.isHeader) {
                    return (
                      <div key={`header-${i}`} className="p-3 font-semibold">
                        {item.title}
                      </div>
                    );
                  } else {
                    return (
                      <Tab key={`tab-${i}`} as={Fragment}>
                        {({ selected }) => (
                          <button
                            key={`btn-${i}`}
                            className={` text-sm font-medium md:block inline-block mb-2 last:mb-0 capitalize ring-0 foucs:ring-0 focus:outline-none px-3 rounded-md py-2 transition duration-150 ${
                              item.nested ? `ml-3 ` : ""
                            } ${
                              selected
                                ? "text-white bg-primary-500 "
                                : "text-slate-500 bg-white"
                            }`}
                          >
                            {item.title}
                          </button>
                        )}
                      </Tab>
                    );
                  }
                })}
              </Tab.List>
            </div>
            <div className="w-full">
              <Tab.Panels>
                <Tab.Panel>
                  <div className="my-4">
                    <label className="form-label">
                      {t("settings.chatbot_id")}: {chatbotID}
                    </label>
                  </div>

                  {/*@ts-ignore*/}
                  <div
                    className={
                      location.hash == "#chatbot-name"
                        ? "bg-slate-200 m-[-10px] p-[10px] rounded-md"
                        : ""
                    }
                  >
                    <Textinput
                      label={t("settings.chatbot_name")}
                      type="text"
                      placeholder=""
                      defaultValue={chatSettings.name}
                      onChange={(e: any) => {
                        let options = Object.assign({}, chatSettings);
                        options.name = e.target.value;
                        setSettings(options);
                      }}
                    />
                  </div>
                  <div className="mt-4">
                    <label className="form-label">{t("settings.conversation_language")}</label>
                    <Select
                      className="react-select"
                      classNamePrefix="select"
                      defaultValue={languagesList.find((item) => {
                        return chatSettings.conversationLanguage == item.value;
                      })}
                      options={languagesList}
                      onChange={(e: any) => {
                        let options = Object.assign({}, chatSettings);
                        options.conversationLanguage = e.value;
                        setSettings(options);
                      }}
                    />
                  </div>
                  <div className="mt-4">
                    {/*@ts-ignore*/}
                    <Textarea
                      label={
                        <div className="flex justify-between items-center">
                          <div>{t("settings.translation_rules")}</div>
                        </div>
                      }
                      placeholder=""
                      row={3}
                      value={chatSettings.translationRules}
                      onChange={(e: any) => {
                        let options = Object.assign({}, chatSettings);
                        options.translationRules = e.target.value;
                        setSettings(options);
                      }}
                    />
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <ModelSettings
                    viewSettings={chatSettings}
                    viewChatbot={viewChatbot}
                    setSettings={setSettings}
                  />
                </Tab.Panel>
                {showSchedule && (
                  <Tab.Panel className={showSchedule ? "" : "hidden"}>
                    <WorkSchedule />
                  </Tab.Panel>
                )}
                <Tab.Panel>
                  <LeadFields
                    ref={leadFormRef}
                    onSave={async (data: Leads, resolver: any) => {
                      let options = Object.assign({}, chatSettings);
                      options.leads = data;
                      setSettings(options);
                      resolver(options);
                    }}
                    {...chatSettings.leads}
                  />
                </Tab.Panel>
                <Tab.Panel>
                  <div className="">
                    {/*@ts-ignore*/}
                    <Textarea
                      label={
                        <span className="flex items-center">
                          {t("settings.domains")}
                          <Tooltip>
                            {t("settings.domains_desc")}
                          </Tooltip>
                        </span>
                      }
                      placeholder=""
                      row={3}
                      value={chatSettings.domains}
                      onChange={(e: any) => {
                        let options = Object.assign({}, chatSettings);
                        options.domains = e.target.value;
                        setSettings(options);
                      }}
                    />
                    <div className="text-xs text-gray-400 mt-1">
                      New domain on a new line
                    </div>
                  </div>
                </Tab.Panel>
                <Tab.Panel>
                  <div className="">
                    The number of messages which can be sent from one device
                  </div>
                  <div className="form-label mt-2">Short limit</div>
                  <div className="mt-2 md:w-3/4 w-full max-w-[700px]">
                    {/*@ts-ignore*/}
                    <div className="flex flex-row justify-between">
                      <div className="relative w-[49%]">
                        <input
                          className="form-control h-10"
                          type="number"
                          value={
                            chatSettings.rateLimits.limit
                              ? chatSettings.rateLimits.limit
                              : ""
                          }
                          onChange={(e: any) => {
                            let options = Object.assign({}, chatSettings);
                            options.rateLimits.limit = parseInt(e.target.value);
                            setSettings(options);
                          }}
                        />
                        <div className="absolute right-10 top-0 h-full flex items-center text-gray-400">
                          messages
                        </div>
                      </div>

                      <div className="relative w-[49%]">
                        <input
                          className="form-control h-10 pl-16"
                          type="number"
                          value={
                            chatSettings.rateLimits.period
                              ? chatSettings.rateLimits.period
                              : ""
                          }
                          onChange={(e: any) => {
                            let options = Object.assign({}, chatSettings);
                            options.rateLimits.period = parseInt(
                              e.target.value,
                            );
                            setSettings(options);
                          }}
                        />
                        <div className="absolute left-2 top-0 h-full flex items-center text-gray-400">
                          every
                        </div>
                        <div className="absolute right-10 top-0 h-full flex items-center text-gray-400">
                          seconds
                        </div>
                      </div>
                    </div>
                    <div className="mt-2">
                      {/*@ts-ignore*/}
                      <Textinput
                        label="Daily limit"
                        type="text"
                        placeholder=""
                        defaultValue={
                          chatSettings.rateLimits.dailyLimit
                            ? chatSettings.rateLimits.dailyLimit
                            : ""
                        }
                        onChange={(e: any) => {
                          let options = Object.assign({}, chatSettings);
                          options.rateLimits.dailyLimit = parseInt(
                            e.target.value,
                          );
                          setSettings(options);
                        }}
                      />
                    </div>
                    <div className="mt-2">
                      {/*@ts-ignore*/}
                      <Textinput
                        label={
                          <span className="normal-case">
                            Show the message to show when a limit is reached
                          </span>
                        }
                        type="text"
                        placeholder=""
                        defaultValue={
                          chatSettings.rateLimits.message
                            ? chatSettings.rateLimits.message
                            : "Too many messages in a row"
                        }
                        onChange={(e: any) => {
                          let options = Object.assign({}, chatSettings);
                          options.rateLimits.message = e.target.value;
                          setSettings(options);
                        }}
                      />
                    </div>
                  </div>
                  <div className="mt-4 w-1/2">{/*@ts-ignore*/}</div>
                </Tab.Panel>
              </Tab.Panels>
            </div>
          </div>
        </Tab.Group>

        <div className="flex justify-end mt-6">
          {/*@ts-ignore*/}
          <Button
            text={t("save_changes")}
            className="btn-primary"
            onClick={async () => {
              if (chatbotID) {
                if (leadFormRef.current) {
                  let formData = await (
                    leadFormRef.current as any
                  ).doSomething();
                  await saveSettings(chatbotID, formData, invalidateChatbot);
                } else {
                  await saveSettings(
                    chatbotID,
                    chatSettings,
                    invalidateChatbot,
                  );
                }
              }
            }}
          />
        </div>
      </Card>
    </div>
  );
};

export default ChatBotSettings;
